<script setup lang="ts">
import { ref } from 'vue'
import { api, extractError } from '@/api/client'
import type { FaqCategory } from '@/types'
import { linkifyText } from '@/utils/linkify'

const categories = ref<FaqCategory[]>([])
const loading = ref(true)
const error = ref('')
const openItems = ref<Set<string>>(new Set())

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.get<{ success: boolean; categories: FaqCategory[] }>('/api/user/faq')
    if (data.success) {
      categories.value = data.categories || []
    }
  } catch (e) {
    error.value = extractError(e, 'Не удалось загрузить FAQ')
  } finally {
    loading.value = false
  }
}

function toggleItem(id: string) {
  const next = new Set(openItems.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  openItems.value = next
}

void load()
</script>

<template>
  <section v-if="loading || categories.length > 0 || error" class="panel mb-4">
    <h2 class="mb-4 text-base font-bold">Частые вопросы</h2>

    <div v-if="loading" class="text-sm text-muted">Загрузка…</div>
    <div v-else-if="error" class="alert alert-danger text-sm">{{ error }}</div>
    <template v-else>
      <div v-for="cat in categories" :key="cat.id" class="mb-5 last:mb-0">
        <h3 class="mb-3 text-sm font-bold text-primary">{{ cat.title }}</h3>
        <div class="divide-y divide-white-light rounded-lg border border-white-light">
          <div v-for="item in cat.items" :key="item.id">
            <button
              type="button"
              class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold transition hover:bg-black/[0.02]"
              :aria-expanded="openItems.has(item.id)"
              @click="toggleItem(item.id)"
            >
              <span>{{ item.question }}</span>
              <i
                class="bi shrink-0 text-muted transition-transform"
                :class="openItems.has(item.id) ? 'bi-chevron-up' : 'bi-chevron-down'"
              />
            </button>
            <div
              v-show="openItems.has(item.id)"
              class="border-t border-white-light bg-black/[0.02] px-4 py-3 text-sm leading-relaxed text-muted"
            >
              <div
                class="whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
                v-html="linkifyText(item.answer)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<style scoped>
:deep(a) {
  word-break: break-all;
}
</style>
